import { Button, ClickAwayListener, Menu, MenuItem } from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hasActiveRoute, routeActive } from '@utils/routes';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useSelector } from 'react-redux';
import { checkPathHasPermission } from '@utils/permissions';
import { colors, typography as typographyTokens, radii } from '@components/design-system/tokens';

import type { RouteSegment } from '@/types/routes';
import type { RootState } from '@features/redux/store';

interface SegmentRouteProps {
    segment: RouteSegment;
}

export const SegmentRoute = ({ segment }: SegmentRouteProps) => {
    const role = useSelector((state: RootState) => state.auth.Roles.role);
    const navigate = useNavigate();
    const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(null);

    const isActive = hasActiveRoute(segment.routes);

    return (
        <React.Fragment>
            <Button
                sx={{
                    textTransform: 'none',
                    fontSize: typographyTokens.fontSize.sm,
                    fontWeight: isActive ? typographyTokens.fontWeight.semibold : typographyTokens.fontWeight.medium,
                    color: isActive ? colors.primary[600] : colors.neutral[600],
                    backgroundColor: isActive ? colors.primary[50] : 'transparent',
                    borderRadius: `${radii.md}px`,
                    padding: '8px 12px',
                    transition: 'all 150ms ease',
                    '&:hover': {
                        backgroundColor: isActive ? colors.primary[100] : colors.neutral[100],
                        color: isActive ? colors.primary[600] : colors.neutral[900],
                    },
                    '& .MuiButton-startIcon': {
                        marginRight: '6px',
                        '& svg': {
                            fontSize: 20,
                            color: isActive ? colors.primary[600] : colors.neutral[500],
                        },
                    },
                    '& .MuiButton-endIcon': {
                        marginLeft: '2px',
                        '& svg': {
                            fontSize: 18,
                            color: colors.neutral[400],
                        },
                    },
                }}
                onClick={(e) => {
                    setDropdownAnchor(dropdownAnchor ? null : e.currentTarget);
                }}
                startIcon={isActive ? segment.IconActive : segment.Icon}
                endIcon={<KeyboardArrowDownIcon />}>
                {segment.name}
            </Button>

            <Menu
                open={Boolean(dropdownAnchor)}
                anchorEl={dropdownAnchor}
                PaperProps={{
                    sx: {
                        mt: 1,
                        borderRadius: `${radii.lg}px`,
                        border: `1px solid ${colors.neutral[200]}`,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        minWidth: 180,
                    },
                }}>
                <ClickAwayListener onClickAway={() => setDropdownAnchor(null)}>
                    <div>
                        {segment.routes.map(
                            (route) =>
                                checkPathHasPermission(role, route.link) && (
                                    <MenuItem
                                        key={route.link}
                                        onClick={() => {
                                            navigate(route.link);
                                            setDropdownAnchor(null);
                                        }}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            padding: '10px 16px',
                                            fontSize: typographyTokens.fontSize.sm,
                                            fontWeight: routeActive(route)
                                                ? typographyTokens.fontWeight.semibold
                                                : typographyTokens.fontWeight.medium,
                                            color: routeActive(route) ? colors.primary[600] : colors.neutral[700],
                                            backgroundColor: routeActive(route) ? colors.primary[50] : 'transparent',
                                            transition: 'all 150ms ease',
                                            '&:hover': {
                                                backgroundColor: routeActive(route)
                                                    ? colors.primary[100]
                                                    : colors.neutral[100],
                                            },
                                            '& svg': {
                                                fontSize: 20,
                                                color: routeActive(route) ? colors.primary[600] : colors.neutral[500],
                                            },
                                        }}>
                                        {routeActive(route) ? route.IconActive : route.Icon}
                                        {route.label}
                                    </MenuItem>
                                )
                        )}
                    </div>
                </ClickAwayListener>
            </Menu>
        </React.Fragment>
    );
};
